import { useCallback, useMemo, useReducer, useRef } from 'react';
import { DirtyOptions, dirtyFields } from './dirtyFields';

/**
 * An edit session: form state, the baseline it is measured against, and the two
 * operations that move that baseline honestly.
 *
 * Every dirty-state bug in the manage-load dialog came from the baseline being
 * moved by accident or not at all, so this exposes exactly two ways to move it
 * and no direct setter.
 *
 * **`applyAutoChange`** — the component reconciling itself. A form that adjusts
 * a field because another field changed has not been edited by anybody, so the
 * baseline moves with the dispatch and dirtiness keeps meaning "a human touched
 * this". A plain `dispatch` from inside an effect is nearly always a bug here.
 *
 * **`rebaseline`** — a save the server confirmed. Deliberately explicit and
 * synchronous. The dialog used to infer this by waiting for the saved row to
 * reappear in the orders list, which silently never happened when the save
 * changed something the list was filtered on: the row dropped out of the result
 * set, the baseline was never refreshed, and a saved load insisted forever that
 * it had unsaved changes.
 */
export interface EditSession<S extends object, A> {
  form: S;
  dispatch: React.Dispatch<A>;
  /** The state the session is currently measured against. */
  baseline: S;
  /** Which fields a human has changed since the baseline. */
  dirty: (keyof S)[];
  isDirty: boolean;
  /** Reconcile a field without counting it as a user edit. */
  applyAutoChange: (action: A, patch: Partial<S>) => void;
  /** Adopt `next` as the new baseline — only after a confirmed save. */
  rebaseline: (next: S) => void;
}

export interface UseEditSessionParams<S extends object, A> extends DirtyOptions<S> {
  reducer: (state: S, action: A) => S;
  initialState: S;
  /**
   * Builds the action that replaces the whole form, e.g.
   * `(state) => ({ type: 'RESET', state })`. Taking this from the caller lets a
   * session wrap an existing reducer without that reducer knowing about it.
   */
  resetAction: (state: S) => A;
}

export function useEditSession<S extends object, A>({
  reducer,
  initialState,
  resetAction,
  ignore,
  comparators,
}: UseEditSessionParams<S, A>): EditSession<S, A> {
  const [form, dispatch] = useReducer(reducer, initialState);

  /*
   * A ref, not state: moving the baseline must never be what triggers a render,
   * or `applyAutoChange` would render twice and the intermediate frame would
   * briefly report the form as dirty. `dirty` recomputes from `form` anyway, and
   * every baseline move happens alongside a dispatch.
   */
  const baselineRef = useRef<S>(initialState);

  const applyAutoChange = useCallback((action: A, patch: Partial<S>) => {
    baselineRef.current = { ...baselineRef.current, ...patch };
    dispatch(action);
  }, []);

  const rebaseline = useCallback(
    (next: S) => {
      /*
       * Copied, and that copy is load-bearing. The usual call is
       * `rebaseline(form)` — the state that was just saved — and dispatching a
       * reset carrying the *same object reference* makes the reducer return
       * identical state, at which point React bails out of the render entirely.
       * `dirty` is memoised on `form`, so without a new reference it would never
       * recompute and the session would stay dirty forever after a clean save:
       * the very bug this hook exists to prevent, reintroduced one layer down.
       */
      const settled = { ...next };
      baselineRef.current = settled;
      dispatch(resetAction(settled));
    },
    [resetAction]
  );

  const dirty = useMemo(
    () => dirtyFields(form, baselineRef.current, { ignore, comparators }),
    // Recomputing on `form` alone is correct: the baseline only ever moves in
    // the same tick as a dispatch, so a change to it always arrives with one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, ignore, comparators]
  );

  return {
    form,
    dispatch,
    baseline: baselineRef.current,
    dirty,
    isDirty: dirty.length > 0,
    applyAutoChange,
    rebaseline,
  };
}
